import styleModule from "./Modals.module.sass";
import PropTypes from "prop-types";
import { Button } from "../Buttons";
import useUnmountComponent from "../../hooks/useUnmountComponent";
import { Suspense, useRef } from "react";
import ReactIconArrow from "../../assets/images/chevron-right.svg?react";
import useAnimationEnd from "../../hooks/useAnimationEnd";
import { ModalBottomBarContext, ModalGeneralContext } from "./ConstModals";
import { useTranslation } from "react-i18next";

const ModalLoad = () => {
  return (
    <div className={styleModule.backgroundModal}>
      <div className={styleModule.containerLogo}>
        <span className={styleModule.bar1}></span>
        <span className={styleModule.bar2}></span>
        <span className={styleModule.bar3}></span>
      </div>
    </div>
  );
};

const ModalConfirmationFooter = props => {
  const {
    unmount,
    message,
    onConfirm,
    disabled,
    extra,
    quitCancel,
    disabledAutoUnmount,
    className
  } = props;

  const handleConfirm = () => {
    onConfirm && onConfirm();
    !disabledAutoUnmount && unmount && unmount();
  };
  const { t } = useTranslation("general");
  return (
    <footer className={`${styleModule.modalConfirmationFooter} ${className}`}>
      <div className={styleModule.message}>{message}</div>
      <section className={styleModule.btnsSections}>
        {extra ? (
          extra
        ) : (
          <>
            {!quitCancel && (
              <Button
                className={styleModule.btnModalConfirm}
                type={"primary-invert"}
                onClick={unmount}
              >
                <Suspense fallback={"Cargando..."}>{t("cancel")}</Suspense>
              </Button>
            )}

            <Button
              className={styleModule.btnModalConfirm}
              type={"primary"}
              onClick={handleConfirm}
              disabled={disabled}
            >
              <Suspense fallback={"Cargando..."}>{t("continue")}</Suspense>
            </Button>
          </>
        )}
      </section>
    </footer>
  );
};

ModalConfirmationFooter.propTypes = {
  unmount: PropTypes.func,
  onConfirm: PropTypes.func,
  message: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node,
    PropTypes.array
  ]).isRequired,
  disabled: PropTypes.bool,
  extra: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.array,
    PropTypes.string
  ]),
  quitCancel: PropTypes.bool,
  disabledAutoUnmount: PropTypes.bool,
  className: PropTypes.string
};

const ModalError = props => {
  const { messages, unmount } = props;
  const wrapperRef = useRef(null);
  useUnmountComponent(wrapperRef, unmount);
  const { t } = useTranslation("general");
  return (
    <div className={styleModule.backgroundModal}>
      <div className={styleModule.containerError} ref={wrapperRef}>
        <h2 className={styleModule.title}>
          <Suspense fallback={"Cargando..."}>{t("oops")}</Suspense>
        </h2>
        <section className={styleModule.content}>
          <ul className={styleModule.messagesList}>
            {messages.map((message, index) => (
              <li key={index} className={styleModule.message}>
                {message}
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
};

ModalError.propTypes = {
  unmount: PropTypes.func,
  messages: PropTypes.array
};

const ModalMessage = props => {
  const { messages, unmount } = props;
  const wrapperRef = useRef(null);
  useUnmountComponent(wrapperRef, unmount);
  // const { t } = useTranslation("general");
  return (
    <div className={styleModule.backgroundModal}>
      <div className={styleModule.containerError} ref={wrapperRef}>
        <h2 className={styleModule.title}>
          <Suspense fallback={"Cargando..."}>{"Aviso"}</Suspense>
        </h2>
        <section className={styleModule.content}>
          <ul className={styleModule.messagesList}>
            {messages.map((message, index) => (
              <li key={index} className={styleModule.message}>
                {message}
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
};

ModalMessage.propTypes = {
  unmount: PropTypes.func,
  messages: PropTypes.array
};

const ModalSideBar = props => {
  const { unmount, children, className } = props;
  const wrapperRef = useRef(null);
  useUnmountComponent(wrapperRef, unmount);
  return (
    <aside
      className={`${styleModule.modalSideBar} ${className}`}
      ref={wrapperRef}
    >
      {children}
    </aside>
  );
};

ModalSideBar.propTypes = {
  unmount: PropTypes.func,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.array]),
  className: PropTypes.string
};

const ModalBottomBar = props => {
  const { unmount, children, className, id } = props;
  const wrapperRef = useRef(null);

  const handleClose = useAnimationEnd({
    backgroundRef: wrapperRef,
    modalRef: wrapperRef,
    unmount,
    fadeIn: styleModule.fadeInBottom,
    fadeOut: styleModule.fadeOutBottom
  });

  return (
    <aside
      className={`${styleModule.modalBottomBar} ${className} ${styleModule.fadeInBottom}`}
      ref={wrapperRef}
      id={id}
    >
      <Button className={styleModule.containerBtnUnmount} onClick={handleClose}>
        <ReactIconArrow />
      </Button>

      <ModalBottomBarContext.Provider
        value={{
          handleClose
        }}
      >
        {children}
      </ModalBottomBarContext.Provider>
    </aside>
  );
};

ModalBottomBar.propTypes = {
  unmount: PropTypes.func,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.array]),
  className: PropTypes.string
};

const ModalGeneral = props => {
  const { children, unmount, className } = props;
  const wrapperRef = useRef(null);
  const modalRef = useRef(null);

  const handleClose = useAnimationEnd({
    backgroundRef: modalRef,
    modalRef: wrapperRef,
    unmount,
    fadeIn: styleModule.fadeIn,
    fadeOut: styleModule.fadeOut
  });

  return (
    <div
      className={`${styleModule.backgroundModal} ${styleModule.modalGeneralBackground} ${styleModule.fadeIn}`}
      ref={modalRef}
    >
      <section
        className={`${styleModule.modalGeneral} ${className}`}
        ref={wrapperRef}
      >
        <ModalGeneralContext.Provider
          value={{
            handleClose
          }}
        >
          {children}
        </ModalGeneralContext.Provider>
      </section>
    </div>
  );
};

ModalGeneral.propTypes = {
  unmount: PropTypes.func,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.array]),
  className: PropTypes.string
};

export {
  ModalBottomBar,
  ModalSideBar,
  ModalLoad,
  ModalConfirmationFooter,
  ModalError,
  ModalGeneral,
  ModalMessage
};
